import React, { Component } from 'react';
import moment from 'moment-timezone';
import styled from 'styled-components';
import { Mutation } from 'react-apollo';
import {
  themes,
  graphql as KBGQL,
  CancelAppointmentResponseData,
  RescheduleAppointmentResponseData
} from 'kb-shared';
import Modal from '../../components/Modal';
import Card from './card';
import { Appointment, TimeSlot } from 'kb-shared';
import CancellationSelection from './components/CancellationSelection';
import BookingCalendarTime from '../Book/components/BookingCalendarTime/BookingCalendarTime';
import { Customization, WhenCustomization } from 'components/WhenCustomization';
import { KindbodyCancellationPolicy } from 'screens/Book/components/KindbodyCancellationPolicy';

type Props = {
  visible: boolean;
  onClose: () => void;
  onReturnToDashboard: Function;
  appointment: Appointment;
  onAppointmentCancelled?: Function;
  onReschedule?: Function;
};

type State = {
  display:
    | 'default'
    | 'confirm_cancel'
    | 'cancelling'
    | 'cancelled'
    | 'error'
    | 'reschedule'
    | 'confirmed';
  cancellationPolicyVisible: boolean;
  error?: string;
  confirmation: string;
  cancellation: {
    reason: string;
    note: string;
  };
  reschedule: {
    id?: number;
    selectedTimeSlot: TimeSlot | null;
  };
};

class ModalCancelResched extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      display: 'default',
      cancellationPolicyVisible: false,
      confirmation: '',
      cancellation: {
        reason: '',
        note: ''
      },
      error: '',
      reschedule: {
        selectedTimeSlot: null
      }
    };
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.visible && !this.props.visible) {
      this.setState({
        display: 'default',
        error: ''
      });
    }
  }

  //#region Methods

  getTitle(): string {
    switch (this.state.display) {
      case 'default':
        return 'Modify Appointment';
      case 'reschedule':
        return 'Reschedule Appointment';
      case 'confirm_cancel':
        return 'Cancel Appointment';
      case 'cancelling':
        return 'Cancelling your appointment.';
      case 'cancelled':
        return 'Your appointment has been canceled.';
      case 'confirmed':
        return 'Confirmation';
      case 'error':
        return 'Sorry, there was a problem...';
      default:
        return '';
    }
  }

  generateButton(name: string, onClick?: Function) {
    let button = {};
    switch (name) {
      case 'cancel':
        button = {
          text: 'Cancel Appointment',
          color: themes.colors.KBWhite,
          onClick: () => {
            this.setState({ display: 'confirm_cancel' });
          }
        };
        break;
      case 'close':
        button = {
          text: 'Close',
          color: themes.colors.KBWhite,
          onClick: () => {
            this.props.onClose();
            this.setState({ error: '' });
          }
        };
        break;
      case 'reschedule':
        button = {
          text: 'Reschedule Appointment',
          color: themes.colors.KBYellow,
          onClick: () => {
            this.setState({ display: 'reschedule' });
          }
        };
        break;
      case 'back':
        button = {
          text: 'Back',
          color: themes.colors.KBWhite,
          onClick: () => {
            this.setState({ display: 'default', error: '' });
          }
        };
        break;

      case 'submit':
        button = {
          text: 'Submit',
          color: themes.colors.KBYellow,
          onClick: () => {}
        };

        break;
    }

    if (button && onClick) {
      // @ts-ignore
      button.onClick = this.submit.bind(this, onClick);
    }

    return button;
  }

  toggleCancelModal = () => {
    this.setState({
      cancellationPolicyVisible: !this.state.cancellationPolicyVisible
    });
  };

  setTimeSlotSelected = (selectedTimeSlot: TimeSlot | null) => {
    this.setState({
      reschedule: { selectedTimeSlot }
    });
  };

  setScreenDetails = (parameters: any) => {
    const { display } = this.state;
    if (display == 'confirm_cancel') this.setState({ cancellation: parameters });
    else if (display == 'reschedule') this.setState({ reschedule: parameters });
  };

  submit(mutationFunction: Function) {
    const { cancellation, reschedule } = this.state;
    const { display } = this.state;
    if (display == 'confirm_cancel') {
      if (cancellation.reason == 'Other - Specify' && cancellation.note.trim().length === 0) {
        this.setState({ error: '*A reason is required*' });
      } else {
        this.setState({ error: '' });
        mutationFunction({
          variables: {
            appointmentId: parseInt(this.props.appointment.id),
            ...cancellation
          }
        })
          .then(() => this.setState({ display: 'confirmed', confirmation: 'cancelled' }))
          .catch(() => {
            this.setState({
              display: 'error',
              error: 'There was an error trying to cancel this appointment. Please try again.'
            });
          });
      }
    }

    if (display == 'reschedule') {
      if (reschedule.selectedTimeSlot) {
        this.setState({ error: '' });
        mutationFunction({
          variables: {
            appointmentId: parseInt(this.props.appointment.id),
            newTimeSlotId: parseInt(reschedule.selectedTimeSlot.id)
          }
        })
          .then(() => {
            this.setState({ display: 'confirmed', confirmation: 'rescheduled' });
          })
          .catch(() => {
            this.setState({
              display: 'error',
              error: 'There was an issue trying to reschedule this appointment'
            });
          });
      } else {
        this.setState({ error: '*Please select a time slot*' });
      }
    }
  }

  //#endregion

  //#region Render Sections

  renderCancellationPolicy() {
    const appointment = this.props.appointment;
    const emailBody = appointment
      ? `&body=%0D%0A%0D%0A%0D%0A%0D%0AAppointment # ${appointment.id}%0D%0A${moment(
          appointment.startTime
        ).format('dddd MMMM d, h:mm A z')}`
      : '';

    return (
      <SubTitle>
        <WhenCustomization is={Customization.NONE}>
          <KindbodyCancellationPolicy emailBody={emailBody} />
        </WhenCustomization>

        <WhenCustomization is={Customization.DISNEY}>
          If you need help rescheduling your appointment, please send us a secure message or call
          the office at 407-934-4100.
        </WhenCustomization>
      </SubTitle>
    );
  }

  renderRescheduleScreen() {
    const { onClose, appointment } = this.props;
    const { selectedTimeSlot } = this.state.reschedule;
    const { error } = this.state;

    //@ts-ignore
    const locationId = parseInt(appointment.location.id);
    const appointmentType = appointment.appointmentType;

    return (
      <Mutation<RescheduleAppointmentResponseData, { appointmentId: number; newTimeSlotId: number }>
        mutation={KBGQL.mutate.RESCHEDULE_APPOINTMENT}
      >
        {rescheduleAppointment => {
          const buttons = appointment.reschedulable
            ? [this.generateButton('back'), this.generateButton('submit', rescheduleAppointment)]
            : [];

          return (
            <Card
              type="mobile-no-padding"
              title={this.getTitle()}
              onClose={onClose}
              buttons={buttons}
            >
              <SubTitle>
                To reschedule your appointment, choose a date and time from the calendar below:
              </SubTitle>
              <BookingCalendarTime
                locationId={locationId}
                appointmentType={appointmentType}
                selectedTimeSlot={selectedTimeSlot}
                onSelectTimeSlot={this.setTimeSlotSelected}
              />
              {error != null && error != '' && <Error>{error}</Error>}
            </Card>
          );
        }}
      </Mutation>
    );
  }

  renderCancellationScreen() {
    const { onClose, appointment } = this.props;
    const { error } = this.state;

    return (
      <Mutation<CancelAppointmentResponseData, { appointmentId: string }>
        mutation={KBGQL.mutate.CANCEL_APPOINTMENT_W_REASON}
      >
        {cancelAppointment => {
          const buttons = appointment.cancelable
            ? [this.generateButton('back'), this.generateButton('submit', cancelAppointment)]
            : [];

          return (
            <Card title={this.getTitle()} onClose={onClose} buttons={buttons}>
              <SubTitle>
                You have chosen to cancel you appointment <br />
                Please select a reason below:
              </SubTitle>
              <CancellationSelection setCancellationInformation={this.setScreenDetails} />
              {error != null && <Error>{error}</Error>}
            </Card>
          );
        }}
      </Mutation>
    );
  }

  renderConfirmationScreen() {
    const { onClose } = this.props;
    let button = this.generateButton('close');
    if (this.state.confirmation == 'cancelled' && this.props.onAppointmentCancelled) {
      button = this.generateButton('close');
      //@ts-ignore
      button.onClick = () => {
        //@ts-ignore
        this.props.onAppointmentCancelled();
        this.setState({ error: '' });
        this.props.onClose();
      };
    } else if (this.state.confirmation == 'cancelled' && this.props.onReschedule) {
      button = this.generateButton('close');
      //@ts-ignore
      button.onClick = () => {
        //@ts-ignore
        this.props.onReschedule();
        this.setState({ error: '' });
        this.props.onClose();
      };
    }

    if (this.state.display == 'error') {
      return (
        <Card title={this.getTitle()} onClose={onClose} buttons={[button]}>
          <SubTitle>{this.state.error}</SubTitle>
        </Card>
      );
    }

    return (
      <Card title={this.getTitle()} onClose={onClose} buttons={[button]}>
        {this.state.confirmation == 'cancelled' && (
          <SubTitle>Your appointment has been canceled.</SubTitle>
        )}
        {this.state.confirmation == 'rescheduled' && (
          <SubTitle>Your appointment has been rescheduled.</SubTitle>
        )}
      </Card>
    );
  }

  renderModificationScreen() {
    const { onClose, appointment } = this.props;
    const buttons = appointment.reschedulable
      ? [this.generateButton('cancel'), this.generateButton('reschedule')]
      : [];

    return (
      <Card title={this.getTitle()} onClose={onClose} buttons={buttons}>
        {this.renderCancellationPolicy()}
      </Card>
    );
  }

  render() {
    const { visible, onClose } = this.props;
    const { display } = this.state;

    return (
      <Modal onRequestClose={onClose} open={visible}>
        <Container>
          {display == 'confirm_cancel' && this.renderCancellationScreen()}
          {display == 'reschedule' && this.renderRescheduleScreen()}
          {display == 'default' && this.renderModificationScreen()}
          {(display == 'confirmed' || display == 'error') && this.renderConfirmationScreen()}
        </Container>
      </Modal>
    );
  }

  //#endregion
}

//#region Styles
const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SubTitle = styled.div`
  ${themes.fonts.FoundersGrotesk};
  font-size: 18px;
  padding: 10px 0px;
`;

const Error = styled.div`
  ${themes.fonts.FoundersGrotesk};
  font-size: 18px;
  padding: 10px 0px;
  color: ${themes.colors.KBError};
`;
//#endregion

export default ModalCancelResched;
